import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Heart } from 'lucide-react';
import React from 'react';
import { useQuoteFavoriteProviders } from '../../hooks/useQuoteFavoriteProviders';
import { Img } from '../Img/Img';

export interface IProviderCardCoverCarouselProps {
  images: string[];
  quoteId?: string;
  profileId: string;
  /** Passed when favoriting so the compare bar can show avatars. */
  profilePhoto?: string;
  profileName?: string;
  grayScale?: boolean;
  responsive?: boolean;
  screenSizes?: any;
  /** When false, heart control is hidden (e.g. missing quote context). */
  showFavorite?: boolean;
}

const SWIPE_THRESHOLD_PX = 40;

export const ProviderCardCoverCarousel: React.FC<
  IProviderCardCoverCarouselProps
> = ({
  images,
  quoteId,
  profileId,
  profilePhoto,
  profileName,
  grayScale,
  responsive,
  screenSizes,
  showFavorite = true,
}) => {
  const [index, setIndex] = React.useState(0);
  const [hasInteracted, setHasInteracted] = React.useState(false);
  const touchStartX = React.useRef<number | null>(null);
  const { isFavorite, toggleFavorite } = useQuoteFavoriteProviders(quoteId);

  const safeImages = images.filter(Boolean);
  const count = safeImages.length;
  const clampedIndex = Math.min(index, Math.max(0, count - 1));

  React.useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(0, count - 1)));
  }, [count]);

  const goNext = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (count <= 1) return;
      setHasInteracted(true);
      setIndex((i) => (i + 1) % count);
    },
    [count]
  );

  const goPrev = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (count <= 1) return;
      setHasInteracted(true);
      setIndex((i) => (i - 1 + count) % count);
    },
    [count]
  );

  const onTouchStart = (e: React.TouchEvent) => {
    setHasInteracted(true);
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (count <= 1) return;
    if (dx < -SWIPE_THRESHOLD_PX) {
      setIndex((i) => (i + 1) % count);
    } else if (dx > SWIPE_THRESHOLD_PX) {
      setIndex((i) => (i - 1 + count) % count);
    }
  };

  const onFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(profileId, { photo: profilePhoto, name: profileName });
  };

  const renderIndexes = React.useMemo(() => {
    if (!count) {
      return [];
    }
    if (!hasInteracted || count === 1) {
      return [clampedIndex];
    }
    const wrapIndex = (i: number) => (i + count) % count;
    return Array.from(
      new Set([
        wrapIndex(clampedIndex - 1),
        clampedIndex,
        wrapIndex(clampedIndex + 1),
      ])
    );
  }, [clampedIndex, count, hasInteracted]);

  if (!count) {
    return null;
  }

  const fav = isFavorite(profileId);

  return (
    <div
      className="absolute inset-0 touch-pan-y"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {renderIndexes.map((i) => {
        const src = safeImages[i];
        return (
          <Img
            key={`${src}-${i}`}
            src={src}
            grayScale={grayScale}
            responsive={responsive}
            screenSizes={screenSizes}
            hideOnError
            className={classNames(
              'absolute top-0 left-0 w-full h-full transition-opacity duration-200',
              i === clampedIndex ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
            )}
            imgClassName="absolute top-0 left-0 w-full h-full object-cover"
          />
        );
      })}

      {showFavorite && !!quoteId && (
        <button
          type="button"
          className="absolute top-2 right-2 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow-sm pointer-events-auto"
          aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={fav}
          onClick={onFavoriteClick}
        >
          <Heart
            className={classNames('h-4 w-4', fav && 'fill-red-500 text-red-500')}
            strokeWidth={fav ? 0 : 2}
          />
        </button>
      )}

      {count > 1 && (
        <>
          {clampedIndex > 0 && (
            <button
              type="button"
              className="absolute left-2 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/25 text-white backdrop-blur-sm transition-colors hover:bg-black/40 pointer-events-auto"
              aria-label="Previous photo"
              onMouseEnter={() => setHasInteracted(true)}
              onClick={goPrev}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
            </button>
          )}
          <button
            type="button"
            className="absolute right-2 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/25 text-white backdrop-blur-sm transition-colors hover:bg-black/40 pointer-events-auto"
            aria-label="Next photo"
            onMouseEnter={() => setHasInteracted(true)}
            onClick={goNext}
          >
            <FontAwesomeIcon icon={faChevronRight} className="text-sm" />
          </button>
          <div
            className="absolute bottom-2 left-0 right-0 z-20 flex justify-center gap-1 pointer-events-none"
            aria-hidden
          >
            {(() => {
              const MAX_DOTS = 5;
              const half = Math.floor(MAX_DOTS / 2);
              const start = Math.min(
                Math.max(0, clampedIndex - half),
                Math.max(0, count - MAX_DOTS)
              );
              const end = Math.min(count, start + MAX_DOTS);
              return Array.from({ length: end - start }, (_, k) => {
                const i = start + k;
                const isActive = i === clampedIndex;
                const isEdge = k === 0 || k === end - start - 1;
                return (
                  <span
                    key={i}
                    className={classNames(
                      'rounded-full transition-all duration-200',
                      isActive
                        ? 'h-1.5 w-1.5 bg-white'
                        : isEdge && count > MAX_DOTS
                        ? 'h-1 w-1 bg-white/35'
                        : 'h-1.5 w-1.5 bg-white/45'
                    )}
                  />
                );
              });
            })()}
          </div>
        </>
      )}
    </div>
  );
};
